import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaWhatsapp,
  FaPhone,
  FaStethoscope,
  FaUserNurse,
  FaAmbulance,
  FaHeartbeat,
  FaStar,
  FaQuoteLeft,
} from 'react-icons/fa';
import { GiMedicinePills } from 'react-icons/gi';

const Home = () => {
  const featuredServices = [
    {
      id: 'home-nursing',
      title: 'Home Nursing',
      description: 'Professional nursing care at your doorstep',
      icon: <FaUserNurse className="text-3xl" />,
      color: 'from-blue-500 to-cyan-500',
    },
    {
      id: 'elderly-care',
      title: 'Elderly Care',
      description: 'Compassionate care for senior citizens',
      icon: <FaHeartbeat className="text-3xl" />,
      color: 'from-green-500 to-emerald-500',
    },
    {
      id: 'physiotherapy',
      title: 'Physiotherapy',
      description: 'Professional therapy sessions at home',
      icon: <FaStethoscope className="text-3xl" />,
      color: 'from-purple-500 to-pink-500',
    },
    {
      id: 'ambulance',
      title: 'Ambulance Support',
      description: '24/7 emergency medical transportation',
      icon: <FaAmbulance className="text-3xl" />,
      color: 'from-red-500 to-orange-500',
    },
  ];

  const testimonials = [
    {
      id: 1,
      name: 'Rajesh Sharma',
      service: 'Home Nursing',
      content: 'Excellent service! The nurse was very professional and caring.',
      rating: 5,
      date: '2024-01-15',
      location: 'Haridwar',
      verified: true,
    },
    {
      id: 2,
      name: 'Priya Singh',
      service: 'Elderly Care',
      content: 'Very reliable and compassionate caregivers for my father.',
      rating: 5,
      date: '2024-01-10',
      location: 'Haridwar',
      verified: true,
    },
  ];

  // helpers used for testimonials (same logic as in Testimonial component)
  const renderStars = (rating) =>
    Array.from({ length: 5 }).map((_, index) => (
      <FaStar
        key={index}
        className={index < rating ? 'text-yellow-400' : 'text-gray-300'}
      />
    ));

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div>
      {/* Hero Banner */}
      <section
  className="relative bg-cover bg-center text-white h-[450px] md:h-[600px]"
  style={{ backgroundImage: "url('/banner.jpg')" }}
>
  {/* Dark Overlay for better text visibility */}
  <div className="absolute inset-0 bg-black/40"></div>

  <div className="container mx-auto px-4 py-20 md:py-28 relative z-10">
    <div className="max-w-3xl">
      <h1 className="text-4xl md:text-6xl font-bold mb-6 font-poppins">
        Quality Home Care at Your Doorstep
      </h1>
      <p className="text-xl mb-8 opacity-90">
        Professional home Home Care services in Haridwar. We bring
        medical care, nursing, and support services right to your home.
      </p>

      <div className="flex flex-wrap gap-4">
        <a
          href="tel:+918266958207"
          className="inline-flex items-center space-x-2 bg-white text-health-blue px-8 py-4 rounded-full font-semibold hover:bg-blue-50 transition-all shadow-lg"
        >
          <FaPhone />
          <span>Book Appointment Now</span>
        </a>

        <a
          href="https://wa.me/918266958207"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center space-x-2 bg-green-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-green-600 transition-all shadow-lg"
        >
          <FaWhatsapp />
          <span>Chat on WhatsApp</span>
        </a>
      </div>
    </div>
  </div>
</section>


      {/* Quick Stats */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { number: '500+', label: 'Patients Served' },
              { number: '50+', label: 'Trained Staff' },
              { number: '24/7', label: 'Emergency Support' },
              { number: '8', label: 'Services Offered' },
            ].map((stat, index) => (
              <div
                key={index}
                className="text-center p-6 bg-white rounded-xl shadow-md"
              >
                <div className="text-3xl font-bold text-health-blue mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 font-poppins">
              Our Home Care Services
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive medical and home care services designed for your
              comfort and well-being
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredServices.map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-2xl shadow-md hover:shadow-xl p-6 border border-gray-100 transition-all duration-300 hover:-translate-y-1"
              >
                <div
                  className={`w-14 h-14 mb-4 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center text-white`}
                >
                  {service.icon}
                </div>

                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  {service.title}
                </h3>

                <p className="text-gray-600 text-sm mb-4">
                  {service.description}
                </p>

                <div className="text-xs text-gray-400">
                  ID: <span className="font-mono">{service.id}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center px-8 py-3 bg-health-blue text-white rounded-full hover:bg-blue-700 transition-colors font-semibold"
            >
              View All Services
              <GiMedicinePills className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 font-poppins">
              What Our Patients Say
            </h2>
            <p className="text-gray-600">Trusted by families across Haridwar</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {testimonials.map((t) => (
              <div
                key={t.id}
                className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                {/* Quote icon */}
                <div className="text-health-blue text-3xl mb-4">
                  <FaQuoteLeft />
                </div>

                {/* Rating */}
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1">{renderStars(t.rating)}</div>
                  <span className="ml-3 text-sm text-gray-500">
                    {t.rating}/5
                  </span>
                </div>

                {/* Content */}
                <p className="text-gray-700 mb-6 italic leading-relaxed">
                  "{t.content}"
                </p>

                {/* Service badge */}
                <div className="inline-block bg-blue-50 text-health-blue text-xs font-semibold px-3 py-1 rounded-full mb-4">
                  {t.service}
                </div>

                {/* Author info */}
                <div className="flex items-center pt-6 border-t border-gray-100">
                  <div className="w-12 h-12 bg-gradient-to-r from-health-blue to-health-green rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {t.name.charAt(0)}
                  </div>

                  <div className="ml-4 text-left">
                    <h4 className="font-bold text-gray-800">{t.name}</h4>
                    <p className="text-sm text-gray-500">
                      {t.location || 'Haridwar'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDate(t.date)}
                    </p>
                  </div>

                  {t.verified && (
                    <div className="ml-auto bg-green-50 text-green-600 text-xs font-semibold px-3 py-1 rounded-full flex items-center">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Verified Patient
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Section */}
      <section className="py-16 bg-gradient-to-r from-red-50 to-orange-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 font-poppins">
            Need Immediate Medical Assistance?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Our emergency team is available 24/7 for ambulance services and
            urgent medical care
          </p>
          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
            <a
              href="tel:+918266958207"
              className="flex items-center space-x-3 bg-red-600 text-white px-8 py-4 rounded-full hover:bg-red-700 transition-colors shadow-lg text-lg font-semibold"
            >
              <FaAmbulance />
              <span>Emergency Call: +91 8266958207</span>
            </a>
            <a
              href="https://wa.me/918266958207"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-3 bg-green-600 text-white px-8 py-4 rounded-full hover:bg-green-700 transition-colors shadow-lg text-lg font-semibold"
            >
              <FaWhatsapp />
              <span>WhatsApp Emergency</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
